import React from "react";
import mapboxgl, { IControl, Map as MapboxMap, Point, GeoJSONSource, Popup } from "mapbox-gl";
import { GeoJsonProperties } from "geojson";

export interface OGControlOptions
{
    layerId?: string,
    doctors: any
    patients: any,
    onMoveEnd: any
}

export class OGControl implements IControl {
    private static readonly DEFAULT_OPTIONS: OGControlOptions = {
        layerId: "olympus-grid-control",
        doctors: [],
        patients: [],
        onMoveEnd: function(){}
    }
    private map?: MapboxMap;
    private container?: any;
    private layerId: string;
    private doctors: any;
    private patients: any;
    private onMoveEnd: any;
    private popup: any;
    constructor(options: OGControlOptions = OGControl.DEFAULT_OPTIONS){
        const controlOptions = Object.assign({}, OGControl.DEFAULT_OPTIONS, options);
        this.layerId = controlOptions.layerId!;
        this.doctors = controlOptions.doctors;
        this.patients = controlOptions.patients;

        this.container = document.createElement('div');
        this.container.className = 'mapboxgl-ctrl og-ctrl';
        this.container.textContent = 'Olympus-Grid';
        this.onMoveEnd = controlOptions.onMoveEnd;
        this.popup = null;
    }
    onAdd(map) {
        this.map = map;
        this.map.on("load", () => {
            this.addMapEvents();
        });
        return this.container;
    }

    onRemove() {
        this.map = undefined;
    }

    addMapEvents(){
        this.map.addSource("doctorsSource", {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: this.doctors,
            },
        });

        this.map.addSource("patientsSource", {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: this.patients,
            },
        });

        this.map.addLayer({
            id: "doctorLayer",
            type: "symbol",
            source: "doctorsSource",
            layout: {
                "icon-image": "marker-icon",
                "icon-size": 0.5,
                "text-field": ["get", "name"],
                "text-offset": [0, 1.2],
                "text-anchor": "top",
            },
        });

        this.map.addLayer({
            id: "patientLayer",
            type: "symbol",
            source: "patientsSource",
            layout: {
                "icon-image": "markerb-icon",
                "icon-size": 0.5,
                "text-field": ["get", "name"],
                "text-offset": [0, 1.2],
                "text-anchor": "top",
            },
        });

        const start = [-87.6430, 41.9026];     // Doctor1
        const end = [-112.0243, 33.5125];     // Patient3
        this.getDriveTime(start, end).then(({ duration, distance, geometry }) => {
            // console.log("🚗 Drive time:", Math.round(duration / 3600), "Hours");
            // console.log("📏 Distance:", (distance / 1000).toFixed(2), "km");
            const routeJson: any = {
                type: "Feature",
                geometry: geometry,
            };
            this.map.addSource("route", {
                type: "geojson",
                data: routeJson,
            });
                
            this.map.addLayer({
                id: "route-line",
                type: "line",
                source: "route",
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                },
                paint: {
                    "line-color": "#3887be",
                    "line-width": 5,
                    "line-opacity": 0.75,
                },
            });
        });

        this.map.on("moveend", () => {
            const doctorVisibleMarkers = this.getVisibleMarkers("doctorsSource");
            const patientVisibleMarkers = this.getVisibleMarkers("patientsSource");
            const results = [];
            doctorVisibleMarkers.map((feature) => {
                results.push(feature);
                return null;
            });
            patientVisibleMarkers.map((feature) => {
                results.push(feature);
                return null;
            });
            this.onMoveEnd(results);
        });

        this.map.on('click', 'doctorLayer', (e) => {
            // console.log(e.features[0]);
            this.map.flyTo({
              center: e.features[0].geometry.coordinates
            });
            this.openMarkerPopup(e.features[0]);
        });

        this.map.on('click', 'patientLayer', (e) => {
            // console.log(e.features[0]);
            this.map.flyTo({
                center: e.features[0].geometry.coordinates
            });
            this.openMarkerPopup(e.features[0]);
        });

        // Change the cursor to a pointer when the it enters a feature in the 'doctorLayer' layer.
        this.map.on('mouseenter', 'doctorLayer', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        // Change it back to a pointer when it leaves.
        this.map.on('mouseleave', 'doctorLayer', () => {
            this.map.getCanvas().style.cursor = '';
        });

        // Change the cursor to a pointer when the it enters a feature in the 'patientLayer' layer.
        this.map.on('mouseenter', 'patientLayer', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        // Change it back to a pointer when it leaves.
        this.map.on('mouseleave', 'patientLayer', () => {
            this.map.getCanvas().style.cursor = '';
        });
    }

    getDriveTime = async (start, end) => {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.join(",")};${end.join(",")}?alternatives=true&geometries=geojson&overview=full&access_token=`+mapboxgl.accessToken;
      
        const res = await fetch(url);
        const data = await res.json();
        // console.log("Found Routes", data);
        const route = data.routes[0];
      
        return {
            distance: route.distance, // in meters
            duration: route.duration, // in seconds
            geometry: route.geometry, // geojson LineString
        };
    };

    getVisibleMarkers(sourceId, layerId = null) {
        const bounds = this.map.getBounds();
        
        if (layerId) {
            // Option 1: Use Mapbox's built-in viewport query (actual rendered features)
            return this.map.queryRenderedFeatures({ layers: [layerId] });
        }
        
        // Option 2: Manual bounding box filter on the GeoJSON source
        const source: GeoJSONSource = this.map.getSource(sourceId);
        if (!source || !source._data) return [];
        let features = this.patients;
        if(sourceId === "doctorsSource"){
            features = this.doctors;
        }
        
        const visible = features.filter((feature) => {
            if (feature.geometry.type !== "Point") return false;
            const [lng, lat] = feature.geometry.coordinates;
            return (
                lng >= bounds.getWest() &&
                lng <= bounds.getEast() &&
                lat >= bounds.getSouth() &&
                lat <= bounds.getNorth()
            );
        });
        
        return visible;
    }

    openMarkerPopup(feature){
        if(this.popup !== null){
            this.popup.remove();
        }
        const coords = feature.geometry.coordinates;
        const properties = feature.properties;
        const name = properties.name || "Untitled";
        const address = properties.address || "Untitled";
        const zipcode = properties.zipcode || "";
        this.map.flyTo({
            center: coords
        });

        const html = "<ul class='marker-info'>"+
                "<li><strong>Name:</strong> "+name+"</li>"+
                "<li><strong>Address:</strong> "+address+"</li>"+
                // "<li><strong>Zipcode:</strong> "+zipcode+"</li>"
                "</ul>";
        this.popup = new Popup()
            .setLngLat(coords)
            .setHTML(html)
            .addTo(this.map);
    }

    async onPinDrop(e, markerType, name, address, zipcode){
        const map = this.map;
        const rect = map.getContainer().getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;
        const point: Point = new Point(clientX - rect.left, clientY - rect.top);
        const lngLat = map.unproject(point);
        const coords = [lngLat.lng, lngLat.lat];

        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?access_token=`+mapboxgl.accessToken;

        const response = await fetch(url);
        const data = await response.json();
        if (data.features.length > 0){
            address = data.features[0].place_name;
        }

        if(name.length <= 0){
            name = `${markerType.charAt(0).toUpperCase() + markerType.slice(1)} ${Math.round(Math.random()*100)}`;
        }
        const feature = {
            type: "Feature",
            geometry: { type: "Point", coordinates: coords },
            properties: {
                name: name,
                address: address,
                zipcode: zipcode,
                type: markerType
            },
        };
    
        let layerName = markerType+"Layer";
        let sourceName = markerType+"sSource";
        let features = this[markerType+"s"];

        features = [...features, feature];
        this[markerType+"s"] = features;
        map.removeLayer(layerName);
        map.removeSource(sourceName);
        map.addSource(sourceName, {
            type: "geojson",
            data: { type: "FeatureCollection", features: features },
        });
        map.addLayer({
            id: layerName,
            type: "symbol",
            source: sourceName,
            layout: {
                "icon-image": (layerName === "doctorLayer" ? "marker-icon" : "markerb-icon"),
                "icon-size": 0.5,
                "text-field": ["get", "name"],
                "text-offset": [0, 1.2],
                "text-anchor": "top"
            }
        });
    }
    onSearch(markerType, query){
        const doctors = this.doctors;
        const patients = this.patients;
        query = query.toLowerCase();
        const results = [];
        doctors.map((feature) => {
            if (feature.geometry.type !== "Point") return false;
            const { name, address, zipcode } = feature.properties;
            if (name.toLowerCase().includes(query) || address.toLowerCase().includes(query) || zipcode === query){
                results.push(feature);
            }
        });
        patients.map((feature) => {
            if (feature.geometry.type !== "Point") return false;
            const { name, address, zipcode } = feature.properties;
            if (name.toLowerCase().includes(query) || address.toLowerCase().includes(query) || zipcode === query){
                results.push(feature);
            }
        });
        return results;
    }
    onResultClick(feature){
        this.map.flyTo({
            center: feature.geometry.coordinates
        });
        this.openMarkerPopup(feature);
    }
};

export default OGControl;